import { Box, BoxProps, Flex } from '@modusign/design-system';
import { useCallback } from 'react';

import './ContextMenuSelect.scss';

type Value = string | number | undefined | object | Array<unknown>;
type Option<Value> = { value: Value; displayText: string };

interface ContextMenuSelectProps<V extends Value, O extends Option<V>> {
  menuName: string;
  options: O[];
  selectedOptionIndex: number;
  onOptionChange: (option: O) => void;
  ariaLabel?: string;
  selectBoxProps?: BoxProps<'div'>;
}

export default function ContextMenuSelect<V extends Value, O extends Option<V>>({
  menuName,
  options,
  selectedOptionIndex,
  ariaLabel,
  onOptionChange,
  selectBoxProps,
}: ContextMenuSelectProps<V, O>) {
  const toKey = useCallback((option: O) => option.displayText, []);

  return (
    <Flex alignItems="center" height="32px" justifyContent="space-between">
      <b>{menuName}</b>
      <Box
        {...selectBoxProps}
        className="ContextMenuSelect__select-box ContextMenuSelect__row-select"
      >
        <select
          aria-label={ariaLabel}
          className="ContextMenuSelect__select"
          data-testid="text-field-font-select"
          defaultValue={selectedOptionIndex}
          onChange={(e) => {
            const option = options[Number(e.target.value)];
            if (option) {
              onOptionChange(option);
            } else {
              throw new Error(
                `option not found. value: ${e.target.value} options: ${options.map(
                  (option) => option.value
                )}`
              );
            }
          }}
        >
          {options.map((option, index) => (
            <option key={toKey(option)} value={index}>
              {options[index].displayText}
            </option>
          ))}
        </select>
      </Box>
    </Flex>
  );
}
